import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useCountry } from '../../components/CountrySelector';
import { useOrderSummary, GET_ORDER_SUMMARY_QUERY } from '../useOrderSummary';
import { useSecondPaymentCompletedTracking } from '../useSecondPaymentCompletedTracking';
import { UpdateOrderAction, OrderState } from '../useUpdateOrder';
import { CHARGE_ORDER_MUTATION, ChargeOrderMutationVariables } from './useChargeOrder.rest';

export const useChargeOrder = (orderNumber: string) => {
  const {
    country: { alpha2Code, alpha3Code }
  } = useCountry();
  const { trackSecondPaymentCompleted } = useSecondPaymentCompletedTracking();

  const { orderSummary } = useOrderSummary(orderNumber);

  const [mutate, { loading, error }] = useMutation(CHARGE_ORDER_MUTATION, {
    refetchQueries: [
      {
        query: GET_ORDER_SUMMARY_QUERY,
        variables: {
          countryCode: alpha2Code,
          orderNumber: orderNumber
        }
      }
    ]
  });

  const chargeOrder = useCallback(
    async (customOrderAttributes?: ChargeOrderMutationVariables['input']['data']['attributes']) => {
      if (!orderSummary) return null;

      const result = await mutate({
        variables: {
          orderId: orderSummary.id,
          input: {
            data: {
              action_type: UpdateOrderAction.Charge,
              id: orderSummary.id,
              type: 'orders',
              attributes: customOrderAttributes
                ? customOrderAttributes
                : {
                    country_code: alpha3Code,
                    order_state: OrderState.Charge,
                    payment_id: orderSummary.attributes.selectedPaymentId,
                    selected_items_ids: orderSummary.attributes.selectedItemsIds || [],
                    use_reward_points: orderSummary.attributes.useMoRewards,
                    use_site_credit: orderSummary.attributes.useSiteCredit
                  }
            }
          }
        }
      });

      trackSecondPaymentCompleted(result.data?.chargeOrder.data[0]);

      return result;
    },
    [alpha3Code, orderSummary, mutate, trackSecondPaymentCompleted]
  );

  return { chargeOrder, loading, error };
};
